import React, { useCallback, useEffect, useState } from 'react';
import { ActionButton, Currency, List, ListItem, Title } from '@icarros/ids-react';
import { useAdContext } from '../../../store/contexts/ad';
import {
  ActionWrapper,
  ListItemModifier,
  OpenEditTitleWrapper,
  OpenEditWrapper,
  PriceAndButtonWrapper,
  PriceInfoWrapper
} from './style';
import { useAd } from '../../../store/hooks/useAd';
import { Loading } from '../../../components/Loading';
import { SaveFormContent } from './SaveFormContent';
import { formatPriceToReal } from '../../../utils/formatters';
import { useFipeRules } from '../../../hooks/useFipeRules';
import { HowToPrices } from './components/Modal';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getPriceSuggestions } from '../../../services/Edits.service';
import { ConfigurationClient } from '../../../services/Configurations.service';
import { GET_CONFIGURATIONS, GET_PRICE_SUGGESTIONS } from '../../../queries';
import isMobile from '../../../utils/is-mobile';

function getMaxInputWarning(value: number) {
  return `o preço não pode ultrapassar ${formatPriceToReal(value)}`;
}

export function PriceForm(): JSX.Element {
  const { setCurrentContent, loading, initialLoading, getCachedContent } = useAd();
  const { currentAd, setValueIsDifferentFromCurrent, cumulativeChanges } = useAdContext();
  const { valorVenda } = currentAd;
  const { km, anoModelo, versao } = currentAd.veiculo;
  const [price, setPrice] = useState<number>(0);
  const [modalOpen, setModalOpen] = useState(false);

  const client = useQueryClient();
  const configQuery = client.getQueryData<ConfigurationClient>([GET_CONFIGURATIONS]);
  const configData = isMobile() ? configQuery?.webmobile : configQuery?.web;

  const getRelevantCumulativeChanges = () => {
    return {
      versionId: cumulativeChanges?.veiculo?.versao?.id ?? versao.id,
      modelYear: cumulativeChanges?.veiculo?.anoModelo ?? anoModelo
    };
  };

  const { modelYear, versionId } = getRelevantCumulativeChanges();

  const { data, isInitialLoading } = useQuery({
    queryKey: [GET_PRICE_SUGGESTIONS, km, modelYear, versionId],
    queryFn: () =>
      getPriceSuggestions({
        km,
        modelYear,
        versionId
      })
  });

  const priceRules = getCachedContent<'priceRules'>('priceRules', {
    path: 'getFipeFeatureFlag',
    dispatchType: 'SET_PRICE_RULES'
  });

  const {
    maxPrice,
    minPrice,
    priceMaxErrorMessage,
    priceMinErrorMessage,
    isMaxActive,
    isMinActive
  } = useFipeRules(data, priceRules) ?? {};

  const getHelperText = useCallback(() => {
    function getHelperText() {
      if (minPrice && isMinActive) {
        if (minPrice > price) return priceMinErrorMessage;
      }
      if (maxPrice && isMaxActive) {
        if (price > maxPrice) return priceMaxErrorMessage;
      }
      if (configData && price > configData.maxValuePrice) {
        return getMaxInputWarning(configData.maxValuePrice);
      }
      return undefined;
    }

    return getHelperText();
  }, [
    maxPrice,
    minPrice,
    priceMaxErrorMessage,
    priceMinErrorMessage,
    price,
    isMinActive,
    isMaxActive,
    configData
  ]);

  useEffect(() => {
    if (!valorVenda) return;
    setPrice(valorVenda);
  }, [valorVenda]);

  useEffect(() => {
    setValueIsDifferentFromCurrent(!getHelperText() && valorVenda !== price);
  }, [getHelperText, price, setValueIsDifferentFromCurrent, valorVenda]);

  return (
    <OpenEditWrapper>
      <OpenEditTitleWrapper>
        <Title size="overline">editar preço do veículo</Title>
        <Title variant="default" contrast="light">
          por quanto você quer vender seu carro?
        </Title>
      </OpenEditTitleWrapper>
      <Currency
        inputLabel="valor de venda"
        onChange={value => value && setPrice(value)}
        initialValue={valorVenda}
        isError={!!getHelperText()}
        helperText={getHelperText()}
      />
      {(initialLoading || isInitialLoading) && <Loading />}

      <PriceAndButtonWrapper>
        {!(initialLoading && (!!getHelperText() || valorVenda === price)) && (
          <SaveFormContent
            isBody
            loading={loading}
            isDisabled={!!getHelperText() || valorVenda === price}
            handleClick={() => {
              const accumulated = cumulativeChanges ?? {};
              setCurrentContent({
                ...accumulated,
                valorVenda: price
              });
            }}
          />
        )}
        {!initialLoading && !isInitialLoading && (
          <PriceInfoWrapper>
            <ActionWrapper>
              <ActionButton
                iconName="itaufonts_informacao"
                text="Como precificar o seu veículo?"
                contrast="default"
                variant="secondary"
                weight="medium"
                onClick={() => setModalOpen(true)}
              />
            </ActionWrapper>
            <HowToPrices modalOpen={modalOpen} setModalOpen={setModalOpen} />

            <List>
              {minPrice && isMinActive ? (
                <ListItemModifier isNormalCase>
                  <ListItem
                    label="Mínimo"
                    variant="simple"
                    descript={formatPriceToReal(minPrice)}
                  />
                </ListItemModifier>
              ) : (
                <></>
              )}
              <ListItemModifier isNormalCase>
                <ListItem
                  label="Média iCarros"
                  variant="simple"
                  descript={data ? formatPriceToReal(data.iCarros) : ''}
                />
              </ListItemModifier>
              <ListItemModifier isNormalCase>
                <ListItem
                  label="Tabela Fipe"
                  variant="simple"
                  descript={data ? formatPriceToReal(data.fipe) : ''}
                />
              </ListItemModifier>
              {maxPrice && isMaxActive ? (
                <ListItemModifier isNormalCase>
                  <ListItem
                    label="Máximo"
                    variant="simple"
                    descript={formatPriceToReal(maxPrice)}
                  />
                </ListItemModifier>
              ) : (
                <></>
              )}
            </List>
          </PriceInfoWrapper>
        )}
      </PriceAndButtonWrapper>
    </OpenEditWrapper>
  );
}
